/**
 * User-facing error messages, keyed by ``error_code``.
 *
 * See ``docs/design/structured-error-reporting-one-pager.md``. This
 * file is the single source of truth for what the SPA renders when
 * a job is in the ``failed`` state. The backend writes a structured
 * ``error_code`` (gRPC status name) on every failed job; we look it
 * up here to render a friendly message — operator-grade text
 * (``Job.error``) is NEVER rendered (C-S4 — leak class closed by
 * separation).
 *
 * Codes come from ``apps/segment_api/errors.py:ErrorCode``. Every
 * value in that enum MUST have an entry here; the unit test in
 * ``errorMessages.test.ts`` asserts coverage and also that no entry
 * contains forbidden infra vocabulary (``RunPod``, ``B2``, ``AWS_``,
 * etc.).
 */

import { INPUT_KIND_V1 } from "./utils/inputKind";

export const USER_MESSAGES: Record<string, string> = {
  CANCELLED:           "Processing was interrupted. Please try again.",
  INVALID_ARGUMENT:    "The request was invalid. Please check your input and try again.",
  DEADLINE_EXCEEDED:   "Job timed out. Please try again.",
  NOT_FOUND:           "No video file found for this job. Please re-upload and try again.",
  PERMISSION_DENIED:   "Your account isn't authorized to use this app. Contact the administrator if you think this is a mistake.",
  FAILED_PRECONDITION: "Cannot process this job in its current state.",
  INTERNAL:            "Processing failed. Please try again.",
  UNAVAILABLE:         "Service is temporarily unavailable. Please try again later.",
  UNAUTHENTICATED:     "You need to be signed in to do that. Please sign in and try again.",
  // Quota cap hit (429). Static copy — the response carries window +
  // resets_at as RFC 7807 extension members for a future dynamic
  // message ("resets Mon Jul 6"); the "Your work" sidebar will render
  // the live count. Anti-leak: must not name the internal limit model
  // (the word "quota" is on the blocklist). See
  // docs/design/quota-tiers-one-pager.md.
  RESOURCE_EXHAUSTED:  "You've reached your video limit for now. Please try again after it resets.",
  // Static copy mentions the default cap (20 min). If
  // YOUTUBE_MAX_DURATION_S is reconfigured server-side the message
  // drifts — accepted for V0 since the cap is unlikely to change
  // frequently. The response carries cap_s as an RFC 7807 extension
  // member for future dynamic rendering.
  YOUTUBE_TOO_LONG:    "Video is longer than 20 minutes. Try a shorter clip or upload a file directly.",
  // Video deleted / private / region-restricted / invalid ID. Actionable
  // (user can upload the file instead); distinct from UNAVAILABLE
  // (service issue, retry later) so the actionable signal isn't lost.
  // Anti-leak: never mentions YouTube Data API, googleapis, quota, API
  // keys, HTTP statuses. Enforced by errorMessages.test.ts FORBIDDEN_VOCABULARY.
  YOUTUBE_UNAVAILABLE: "This YouTube video isn't available. It may be private, removed, or region-restricted. Try uploading the file instead.",
  // Fallback only — the SPA intercepts 409 + YOUTUBE_DUPLICATE in
  // NewJobForm and renders a dedicated modal with "Open existing" /
  // "Run again" actions. If a future call site forwards a duplicate
  // response into the generic error renderer, this string keeps the
  // user oriented. See docs/design/youtube-reingest-dedupe-one-pager.md.
  YOUTUBE_DUPLICATE:   "You've already analyzed this video.",
  // Anti-leak: never names NVDEC / cuvid / PyAV / RunPod / GPU model.
  // Actionable: retry is meaningful (the worker may land on a different
  // host) so we surface that. See
  // docs/design/cpu-only-pyav-detect-path-one-pager.md §Failure surface.
  //
  // All three read identically: to a customer this is one situation — we
  // could not process their video, and retrying usually lands somewhere
  // that can. The distinct codes exist for operators, not users (issue
  // #1113 for the memory preflight).
  //
  // These previously said "Video decoding wasn't available for this clip"
  // and "Processing couldn't start safely". Both were rejected in review
  // of issue #1648: "decoding" is pipeline vocabulary and "safely" leaks
  // the startup-check concept (and implies the customer's video was
  // unsafe). Neither was actionable. Both words are now in
  // FORBIDDEN_VOCABULARY.
  GPU_DETECT_UNAVAILABLE: "We couldn't process this video. Please try again, or contact support if this persists.",
  GPU_DECODE_PREFLIGHT_FAILED: "We couldn't process this video. Please try again, or contact support if this persists.",
  GPU_MEMORY_PREFLIGHT_FAILED: "We couldn't process this video. Please try again, or contact support if this persists.",
};

/**
 * Shown for failed jobs where ``error_code`` is null or unrecognised.
 * Falls back to a deliberately generic message rather than reading
 * ``Job.error`` (which would re-leak the operator strings this
 * contract fixes — C-S5).
 */
export const FALLBACK_MESSAGE = "Job failed. Please contact support.";

/**
 * Resolve the user-facing message for a job's error_code. Returns
 * ``FALLBACK_MESSAGE`` when the code is null/undefined or not in
 * ``USER_MESSAGES``.
 */
export function userMessageForCode(errorCode: string | null | undefined): string {
  if (!errorCode) return FALLBACK_MESSAGE;
  return USER_MESSAGES[errorCode] ?? FALLBACK_MESSAGE;
}

/**
 * RallyClip v1 verified-malformed-bundle copy (issue #1866;
 * docs/design/rallyclip-v1/api-schemas.md "Retry a failed job").
 *
 * Kept OUTSIDE ``USER_MESSAGES`` on purpose: that table is keyed by
 * ``error_code`` alone and is parity-checked character-for-character
 * against iOS by ``apps/ios_video_segmentation/tests/
 * test_job_error_messages.py``. This message is selected by the saved
 * ``input_kind`` AND ``error_code`` pair, so it lives in its own lookup
 * and leaves v0's ``INVALID_ARGUMENT`` copy untouched.
 */
export const V1_INVALID_INPUT_MESSAGE =
  "The detection file is invalid. Start analysis again from the original video.";

/** The two saved fields the message choice reads. */
export interface FailedJobLike {
  input_kind?: string | null;
  error_code: string | null | undefined;
}

/**
 * The failed-panel message for a job: the v1 detection-file copy for a
 * saved ``input_kind=device_detections_v1`` + ``error_code=
 * INVALID_ARGUMENT`` pair, otherwise exactly ``userMessageForCode``.
 * An absent ``input_kind`` is v0 (the API omits the key for v0), so
 * every existing v0 message is unchanged.
 */
export function userMessageForJob(job: FailedJobLike): string {
  if (
    job.input_kind === INPUT_KIND_V1 &&
    job.error_code === "INVALID_ARGUMENT"
  ) {
    return V1_INVALID_INPUT_MESSAGE;
  }
  return userMessageForCode(job.error_code);
}

/**
 * Generic message for non-Problem-Details error responses (HTML
 * maintenance pages, B2/S3 XML errors, plain-text upstream failures,
 * body stream errors). Status code is included for support
 * escalation; the response body is NEVER rendered.
 */
export function genericNetworkError(status: number): string {
  return `Something went wrong (${status}). Please try again.`;
}

/**
 * The shape every error-render site consumes. ``message`` is the
 * user-facing string (resolved via USER_MESSAGES / FALLBACK_MESSAGE /
 * genericNetworkError); ``traceId`` is the operator-pasteable Jaeger
 * key when the backend supplied one (header ``X-Trace-Id`` or body
 * ``trace_id`` field; header is preferred).
 *
 * ``traceId`` is undefined when tracing is disabled, when the response
 * came from a layer that doesn't propagate it (CORS preflight, parse
 * failure on a non-Problem-Details body), or when both surfaces returned
 * the all-zero ``00000000…`` sentinel.
 *
 * Display rule: render ``message`` always; render
 * ``Reference: <traceId-truncated>…`` below the message iff ``traceId``
 * is defined. See ``docs/design/error-correlation-one-pager.md`` §A.5.
 */
export interface UserFacingError {
  message: string;
  traceId?: string;
}

const ZERO_TRACE_ID = "00000000000000000000000000000000";

/**
 * Extract a usable trace_id from the response. Header is preferred —
 * it survives every error path (CORS, parse failure, proxy 502 with no
 * Problem Details body); the body field is the SPA fast-path. If both
 * are present they should match (asserted by integration test); we
 * return the header value either way for consistency.
 *
 * Returns undefined when neither surface has a usable value (missing,
 * malformed, or all-zero sentinel). Caller renders nothing.
 */
function extractTraceId(resp: Response, body: unknown): string | undefined {
  const headerValue = resp.headers.get("X-Trace-Id");
  if (headerValue) {
    const normalised = headerValue.toLowerCase().trim();
    if (/^[0-9a-f]{32}$/.test(normalised) && normalised !== ZERO_TRACE_ID) {
      return normalised;
    }
  }
  if (body && typeof body === "object" && "trace_id" in body) {
    const bodyValue = (body as { trace_id?: unknown }).trace_id;
    if (typeof bodyValue === "string") {
      const normalised = bodyValue.toLowerCase().trim();
      if (/^[0-9a-f]{32}$/.test(normalised) && normalised !== ZERO_TRACE_ID) {
        return normalised;
      }
    }
  }
  return undefined;
}

/**
 * Convert a non-OK fetch Response into a user-facing error object —
 * the synchronous-channel counterpart of C-S4 (see
 * ``docs/design/structured-error-reporting-one-pager.md``).
 *
 * - If the response is RFC 7807 Problem Details JSON with a known
 *   ``code``, returns ``{ message: USER_MESSAGES[code], traceId? }``.
 * - Otherwise (HTML, XML, malformed JSON, network error pages),
 *   returns ``{ message: genericNetworkError(status), traceId? }``.
 *
 * ``traceId`` is sourced from the ``X-Trace-Id`` header first, body
 * field second; undefined when neither has a valid hex trace_id.
 *
 * NEVER renders the raw response body. The backend's Problem Details
 * middleware deliberately omits ``detail`` from the body (C-S7), but
 * the body still contains machine-readable metadata (``code``,
 * ``title``, ``trace_id``) that's appropriate for tooling, not end
 * users. The body stream is read once internally; the caller MUST NOT
 * call ``.text()`` / ``.json()`` on the response again.
 */
export async function readUserFacingError(resp: Response): Promise<UserFacingError> {
  // Only attempt JSON parsing when the content-type advertises it.
  // Saves an exception path on the common case where Railway returns
  // an HTML maintenance page or B2 returns XML.
  const contentType = resp.headers.get("content-type") ?? "";
  let body: unknown = undefined;
  if (contentType.includes("json")) {
    try {
      body = await resp.json();
    } catch (err) {
      // Body wasn't valid JSON despite content-type, or stream errored.
      // Log for dev visibility (an unexpected case worth investigating)
      // but fall through to the generic message — the user always sees
      // a sensible string.
      // eslint-disable-next-line no-console
      console.warn(
        "readUserFacingError: JSON parse failed despite content-type=%s; falling back to generic",
        contentType,
        err,
      );
    }
  }
  let traceId = extractTraceId(resp, body);
  let code: string | undefined;
  if (body && typeof body === "object" && "code" in body) {
    const c = (body as { code?: unknown }).code;
    if (typeof c === "string") code = c;
  }
  // Decisions §D6: don't surface Reference: on UNAUTHENTICATED — user's
  // next action is re-login, not report-to-operator. Dropping traceId at
  // the data layer keeps the rule in code (not just docs), so a future
  // render site that consumes UserFacingError can't accidentally show
  // Reference: on a re-login toast. Other codes get the reference per
  // §A.4.
  if (code === "UNAUTHENTICATED") {
    traceId = undefined;
  }
  if (code !== undefined) {
    return { message: userMessageForCode(code), traceId };
  }
  return { message: genericNetworkError(resp.status), traceId };
}
